/**
 * COMMUNICATION — types et petites primitives partagées du module.
 * Miroir du modèle serveur (`packages/core/src/communication`) : apps/web
 * n'a aucune dépendance interne, les types sont recopiés à l'identique.
 */

export type CommunicationPeriod = 'TODAY' | 'SEVEN_DAYS' | 'THIS_WEEK' | 'THIRTY_DAYS' | 'NINETY_DAYS'

export const PERIODES: { value: CommunicationPeriod; label: string }[] = [
  { value: 'TODAY', label: 'Aujourd’hui' },
  { value: 'SEVEN_DAYS', label: '7 jours' },
  { value: 'THIS_WEEK', label: 'Cette semaine' },
  { value: 'THIRTY_DAYS', label: '30 jours' },
  { value: 'NINETY_DAYS', label: '90 jours' },
]

export interface HonestDelta {
  state: 'OK' | 'NO_HISTORY'
  current: number
  previous: number | null
  abs: number | null
  pct: number | null
  availableInDays: number | null
}

export interface InstagramOverview {
  connected: boolean
  owner_connected: boolean
  reach_week: HonestDelta | null
  interactions_week: HonestDelta | null
  health: string
  username: string | null
  name: string | null
  followers: number | null
  followersDelta: HonestDelta | null
  series: { day: string; value: number }[]
  lastSyncAt: string | null
  topContent: { id: string; kind: string | null; caption: string | null; like_count: number | null; comments_count: number | null; thumbnail_url: string | null; preview_asset_id: string | null; published_at: string | null }[]
}

export interface GoogleOverview {
  connected: boolean
  health: string
  locationTitle: string | null
  impressions: HonestDelta | null
  maps: number | null
  search: number | null
  actions: { key: string; label: string; delta: HonestDelta }[]
  series: { day: string; value: number }[]
  reviews: { average_rating: number | null; total: number; new_this_week: number; to_answer: number }
  lastSyncAt: string | null
}

export interface CommunicationOverview {
  safeMode: boolean
  instagram: InstagramOverview
  google: GoogleOverview
  publications: { scheduled: number; drafts: number; awaiting: number }
}

export const PUBLICATION_STATE_FR: Record<string, string> = {
  DRAFT: 'Brouillon', READY: 'Prêt', SCHEDULED: 'Programmé',
  AWAITING_WRITE_ACCESS: 'En attente d’autorisation d’écriture',
  PUBLISHING: 'Publication en cours', PUBLISHED: 'Publié',
  FAILED: 'Échec', UNCERTAIN: 'À vérifier', CANCELLED: 'Annulé',
}

export const HEALTH_FR: Record<string, string> = {
  OPERATIONAL: 'Opérationnel', ACTION_REQUIRED: 'Action requise',
  RECONNECT: 'Connexion à renouveler', PARTIAL: 'Partiel', NOT_CONFIGURED: 'Non configuré',
}

export function nombreFr(n: number): string {
  return n.toLocaleString('fr-FR')
}

export function fraicheurFr(iso: string | null): string {
  if (!iso) return 'jamais synchronisé'
  const s = (Date.now() - new Date(iso).getTime()) / 1000
  if (s < 90) return 'à l’instant'
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`
  if (s < 86400) return `il y a ${Math.round(s / 3600)} h`
  return `il y a ${Math.round(s / 86400)} j`
}

/** Le signe et la classe sémantique d'un delta — jamais toute la page en
 *  rouge pour un −4 % (§94). */
export function deltaClasse(d: HonestDelta | null): 'up' | 'down' | 'flat' | 'none' {
  if (!d || d.state !== 'OK' || d.abs === null) return 'none'
  return d.abs > 0 ? 'up' : d.abs < 0 ? 'down' : 'flat'
}

export function deltaTexte(d: HonestDelta | null): { abs: string; pct: string | null } | null {
  if (!d || d.state !== 'OK' || d.abs === null) return null
  const signe = d.abs > 0 ? '+' : d.abs < 0 ? '−' : ''
  return {
    abs: `${signe}${nombreFr(Math.abs(d.abs))}`,
    pct: d.pct === null ? null : `${d.pct > 0 ? '+' : d.pct < 0 ? '−' : ''}${Math.abs(d.pct).toLocaleString('fr-FR')} %`,
  }
}

/* ═══════════════ types secondaires (miroir serveur) ═════════════════════ */

export interface ContentRow {
  id: string
  provider_id: string
  kind: string | null
  caption: string | null
  permalink: string | null
  thumbnail_url: string | null
  media_url: string | null
  published_at: string | null
  like_count: number | null
  comments_count: number | null
}

export interface ReviewRow {
  review_id: string
  rating: number
  comment: string | null
  create_time: string | null
  update_time: string | null
  reviewer_display_name: string | null
  replied: boolean
  reply_comment: string | null
  reply_update_time: string | null
  reply_draft: string | null
  unavailable_at: string | null
}

export interface ReviewHeadline {
  average_rating: number | null
  total: number
  new_this_week: number
  to_answer: number
}

export interface PublicationRow {
  id: string
  state: string
  title: string
  body: string
  channels: string[]
  adaptations: Record<string, { title?: string; body?: string }>
  scheduled_at: string | null
  source_event_id: string | null
  event_payload: Record<string, unknown> | null
  provider_results: Record<string, { id?: string; state?: string; at?: string; reason?: string }>
  created_at: string
  updated_at: string
}

export interface DiagnosticStep { key: string; title: string; done: boolean; detail: string | null }

export interface CapabilityLine {
  capability: string
  state: string
  label: string
  state_label: string
  detail?: string | null
}

/** `apiGet` RÉSOUT sur une erreur HTTP — on valide donc la FORME avant de
 *  rendre (leçon du Centre d'activité, `/activite/<uuid>` écran noir). */
export function estErreurApi(x: unknown): x is { error: string; message?: string } {
  return typeof x === 'object' && x !== null && typeof (x as { error?: unknown }).error === 'string'
}

export function heureFr(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('fr-FR', {
    timeZone: 'Europe/Paris', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

export function jourFr(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('fr-FR', {
    timeZone: 'Europe/Paris', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })
}
